/**
 * Formats a number as currency (USD).
 * Handles null or undefined values gracefully.
 *
 * @param value - The number to format. Can be null or undefined.
 * @param placeholder - The string to return if the value is null or undefined (defaults to '$0.00').
 * @returns The formatted currency string or the placeholder.
 */
export function formatCurrency(
  value: number | null | undefined,
  placeholder = '$0.00',
  includeCents = false,
  roundToThousands = false
): string {
  // Check specifically for null or undefined, allow 0
  if (value === null || value === undefined || isNaN(value)) {
    // Return placeholder for null/undefined/NaN, including if 0 should be placeholder
    if (placeholder === '$0.00' && value === 0) {
      return '$0.00';
    }
    return placeholder;
  }

  if (roundToThousands) {
    value = Math.round(value / 1000) * 1000;
  }

  return value.toLocaleString('en-US', {
    style: 'currency',
    currency: 'USD',
    minimumFractionDigits: includeCents ? 2 : 0,
    maximumFractionDigits: includeCents ? 2 : 0,
  });
}

// Helper to format date as YYYY-MM
export function formatDateYYYYMM(date: Date): string {
  const year = date.getFullYear();
  const month = (date.getMonth() + 1).toString().padStart(2, '0'); // Month is 0-indexed
  return `${String(year)}-${month}`; // Convert year to string
}

// Helper to format date as MMM YYYY
export function formatDateMMMMYYYY(date: Date): string {
  return date.toLocaleString('default', { month: 'long', year: 'numeric' });
}

// Helper to format date as MMM YYYY
export function formatDateMMMYYYY(date: Date): string {
  return date.toLocaleString('default', { month: 'short', year: 'numeric' });
}

/**
 * Formats a number as a percentage string.
 *
 * @param value - The number to format (e.g., 0.15 for 15%).
 * @param decimalPlaces - The number of decimal places to include (defaults to 1).
 * @param placeholder - The string to return if the value is null, undefined, or NaN (defaults to '--%').
 * @returns The formatted percentage string or the placeholder.
 */
export function formatPercentage(
  value: number | null | undefined,
  decimalPlaces = 1,
  placeholder = '--%'
): string {
  if (value === null || value === undefined || isNaN(value)) {
    return placeholder;
  }
  return (
    (value * 100).toLocaleString('en-US', {
      minimumFractionDigits: decimalPlaces,
      maximumFractionDigits: decimalPlaces,
    }) + '%'
  );
}
